'use client';
import React, { useState, useEffect } from 'react';
import TopNav from '../components/TopNav';
import MarketTicker from '../components/MarketTicker';
import FooterBar from '../components/FooterBar';
import { Play, Radio, Users, Maximize2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface TvShow {
  id: string;
  show_title: string;
  host_name: string;
  start_time: string;
  end_time: string;
  category_tag: string;
  is_live: boolean;
  schedule_date: string;
}

function formatTime(t: string) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm} EST`;
}

export default function LiveTVPage() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [shows, setShows] = useState<TvShow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();

    const fetchShows = async () => {
      const today = new Date().toISOString().split('T')[0];
      const { data } = await supabase
        .from('tv_schedule')
        .select('*')
        .eq('schedule_date', today)
        .order('start_time', { ascending: true });
      if (data) setShows(data);
      setLoading(false);
    };

    fetchShows();

    const channel = supabase
      .channel('livetv_schedule')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tv_schedule' }, fetchShows)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const activeShow = shows.find((s) => s.is_live);

  const displayShows = shows.length > 0 ? shows : [
    { id: 'ls-1', show_title: 'Crypto Morning Briefing', host_name: 'Alex Rivera', start_time: '09:00', end_time: '10:00', category_tag: 'News', is_live: true, schedule_date: '' },
    { id: 'ls-2', show_title: 'Market Analysis Deep Dive', host_name: 'Sarah Chen', start_time: '14:00', end_time: '15:30', category_tag: 'Analysis', is_live: false, schedule_date: '' },
    { id: 'ls-3', show_title: 'DeFi Weekly Roundup', host_name: 'Marcus Johnson', start_time: '16:00', end_time: '17:00', category_tag: 'DeFi', is_live: false, schedule_date: '' },
    { id: 'ls-4', show_title: 'Bitcoin Price Watch', host_name: 'Emma Williams', start_time: '18:00', end_time: '19:00', category_tag: 'Bitcoin', is_live: false, schedule_date: '' },
    { id: 'ls-5', show_title: 'Altcoin Spotlight', host_name: 'David Park', start_time: '20:00', end_time: '21:00', category_tag: 'Altcoins', is_live: false, schedule_date: '' },
  ];

  const liveShow = displayShows.find((s) => s.is_live) || displayShows[0];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />
      <MarketTicker />
      <main className="w-full max-w-screen-2xl mx-auto px-4 lg:px-6 xl:px-8 2xl:px-10 py-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 rounded-full bg-primary live-pulse" />
            <span className="text-primary text-xs font-bold uppercase tracking-widest">Broadcasting Live</span>
          </div>
          <h1 className="text-3xl font-black text-foreground">DMTV Live</h1>
          <p className="text-muted-foreground text-sm mt-1">24/7 live crypto news, market analysis, and expert commentary.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Main Player */}
          <div className="lg:col-span-2">
            <div className="relative bg-[#0D1A0D] rounded-lg overflow-hidden aspect-video flex items-center justify-center border border-border group cursor-pointer" onClick={() => setIsPlaying(!isPlaying)}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#0D1A0D] via-[#0A0A0A] to-[#1A0D0D]" />
              <div className="absolute inset-0 opacity-20">
                {[...Array(8)]?.map((_, i) => (
                  <div key={i} className="absolute bottom-0 bg-crypto-green/60 rounded-t" style={{ left: `${10 + i * 11}%`, width: '8%', height: `${20 + Math.sin(i) * 30 + 20}%` }} />
                ))}
              </div>

              <div className="absolute top-4 left-4 flex items-center gap-2 bg-primary px-3 py-1.5 rounded">
                <span className="w-2 h-2 rounded-full bg-white live-pulse" />
                <span className="text-white text-xs font-bold uppercase tracking-wider">Live</span>
              </div>

              <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-black/70 px-3 py-1.5 rounded">
                <Users size={12} className="text-white" />
                <span className="text-white text-xs font-semibold">12.4K watching</span>
              </div>

              <div className={`relative z-10 flex flex-col items-center gap-3 transition-opacity duration-300 ${isPlaying ? 'opacity-0' : 'opacity-100'}`}>
                <div className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <Play size={28} fill="white" className="text-white ml-1" />
                </div>
                <span className="text-white text-sm font-semibold">Watch Live</span>
              </div>

              {isPlaying && (
                <div className="relative z-10 flex flex-col items-center gap-2">
                  <Radio size={32} className="text-crypto-green live-pulse" />
                  <span className="text-white text-sm font-semibold">{liveShow?.show_title}</span>
                  <span className="text-gray-400 text-xs">with {liveShow?.host_name}</span>
                </div>
              )}

              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-semibold text-sm">{liveShow?.show_title}</p>
                <p className="text-gray-400 text-xs">with {liveShow?.host_name} • {liveShow?.is_live ? 'Live Now' : formatTime(liveShow?.start_time)}</p>
              </div>

              <button className="absolute bottom-4 right-4 p-1.5 bg-black/50 rounded hover:bg-black/70 transition-colors duration-150">
                <Maximize2 size={14} className="text-white" />
              </button>
            </div>

            <div className="mt-3 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-foreground">{liveShow?.show_title}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">Live crypto news and market analysis with {liveShow?.host_name}.</p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Users size={13} />
                <span>12.4K live</span>
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <h2 className="text-base font-semibold text-foreground">Today's Schedule</h2>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground text-xs">Loading schedule…</div>
            ) : (
              <div className="divide-y divide-border/50">
                {displayShows?.map((show) => (
                  <div key={show?.id} className={`px-4 py-3 transition-colors duration-150 ${show?.is_live ? 'bg-primary/10' : 'hover:bg-muted/30'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          {show?.is_live && (
                            <span className="flex items-center gap-1 text-[10px] font-bold text-primary">
                              <span className="w-1.5 h-1.5 rounded-full bg-primary live-pulse flex-shrink-0" />
                              ACTIVE NOW
                            </span>
                          )}
                          <span className={`text-xs font-bold uppercase tracking-wider ${show?.is_live ? 'text-primary' : 'text-muted-foreground'}`}>
                            {show?.is_live ? 'LIVE NOW' : formatTime(show?.start_time)}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-foreground truncate">{show?.show_title}</p>
                        <p className="text-xs text-muted-foreground">{show?.host_name}</p>
                      </div>
                      <span className="text-[10px] bg-muted text-muted-foreground px-2 py-0.5 rounded flex-shrink-0">{show?.category_tag}</span>
                    </div>
                    {show?.is_live && (
                      <div className="flex items-center gap-1 mt-1.5">
                        <Users size={11} className="text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">12.4K watching</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <FooterBar />
    </div>
  );
}
